import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CognitoService } from '../services/cognito.service';
import { from, Observable, throwError, BehaviorSubject } from 'rxjs';
import { switchMap, catchError, filter, take, finalize } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(private readonly cognitoService: CognitoService) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    const bypassUrls = [
      '/admin-users/forget-password',
      'admin/login-2fa-send-otp',
      'admin/login-2fa-verify-otp',
      '/admin-users/set-forget-password'
    ];

    // Skip authentication for bypass URLs
    if (bypassUrls.some((url) => req.url.includes(url))) {
      return next.handle(req);
    }

    // Add token to request and handle 401 errors
    return from(this.cognitoService.getAccessToken()).pipe(
      switchMap((accessToken) => {
        const authReq = this.addTokenToRequest(req, accessToken);
        return next.handle(authReq).pipe(
          catchError((error: HttpErrorResponse) => {
            // Handle 401 Unauthorized errors
            if (error.status === 401) {
              return this.handle401Error(authReq, next);
            }
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        // Handle token fetch errors
        console.error('Error getting access token:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Add authorization token to request
   */
  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  /**
   * Handle 401 Unauthorized errors
   * This method ensures only one token refresh happens at a time
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return from(this.cognitoService.refreshAccessToken()).pipe(
        switchMap((newToken: string) => {
          this.refreshTokenSubject.next(newToken);
          // Retry the original request with new token
          return next.handle(this.addTokenToRequest(request, newToken));
        }),
        catchError((error) => {
          // Refresh failed - user will be logged out by CognitoService
          console.error('Token refresh failed in interceptor:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      // If refresh is already in progress, wait for it to complete
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          // Retry the original request with the new token
          return next.handle(this.addTokenToRequest(request, token!));
        })
      );
    }
  }
}